import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiAlertTriangle,
  FiArrowUpRight,
  FiBriefcase,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiLink,
  FiPlus,
  FiSearch,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiZap,
  FiX,
} from 'react-icons/fi';
import {
  AccountActivityInput,
  AccountContactInput,
  AccountInput,
  AccountMeta,
  AccountOpportunityInput,
  AccountSalesStage,
  AccountSignalInput,
  EliteCoreAccount,
  createAccount,
  createAccountActivity,
  createAccountContact,
  createAccountOpportunity,
  createAccountSignal,
  getAccount,
  getAccountMeta,
  linkAccountInstance,
  listAccounts,
  unlinkAccountInstance,
  updateAccount,
  updateAccountSignal,
} from '../../api/accounts';
import {
  CommandCentreInstance,
  CommandCentreInstanceUser,
  listInstances,
  listInternalUsers,
} from '../../api/instances';
import { useAuth } from '../../auth/AuthProvider';
import './AccountsPage.css';

const DEFAULT_META: AccountMeta = {
  relationships: ['lead', 'prospect', 'customer', 'former-customer', 'partner'],
  salesStages: [
    'new',
    'contacted',
    'qualified',
    'discovery',
    'demo-scheduled',
    'demo-completed',
    'evaluation',
    'migration-assessment',
    'proposal',
    'negotiation',
    'won',
    'lost',
  ],
  customerStates: ['onboarding', 'implementing', 'live', 'expanding'],
  healthStatuses: ['healthy', 'attention', 'at-risk'],
  opportunityStatuses: ['open', 'won', 'lost', 'paused'],
  activityTypes: [
    'note',
    'email',
    'call',
    'meeting',
    'demo',
    'website-lead',
    'migration-assessment',
    'proposal',
    'follow-up',
    'stage-change',
    'contact-added',
    'opportunity-created',
    'opportunity-updated',
    'instance-linked',
    'instance-unlinked',
    'signal-added',
    'signal-resolved',
    'system',
  ],
  activityDirections: ['inbound', 'outbound', 'internal'],
  signalTypes: [
    'requirement',
    'objection',
    'pain-point',
    'buying-signal',
    'competitor',
    'decision-criterion',
    'migration-risk',
    'product-interest',
  ],
  signalStatuses: ['active', 'resolved', 'invalid'],
};

const EMPTY_NEW_ACCOUNT: AccountInput = {
  name: '',
  website: '',
  relationship: 'lead',
  salesStage: 'new',
  source: '',
  currentSystem: '',
  estimatedArr: null,
  ownerUserId: null,
};

const titleCase = (value?: string | null) =>
  String(value || '')
    .split('-')
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : '')
    .join(' ');

const formatMoney = (value?: number | null) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const isActiveProspect = (account: EliteCoreAccount) =>
  ['lead', 'prospect'].includes(account.relationship) && account.salesStage !== 'lost';

const formatActivityTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const scoreTone = (level?: string | null) =>
  level === 'high' ? 'high' : level === 'medium' ? 'medium' : 'low';

const AccountsPage: React.FC = () => {
  const { user } = useAuth();
  const isGlobal = user?.commandCentreAccess?.scope === 'global';

  const [meta, setMeta] = useState<AccountMeta>(DEFAULT_META);
  const [accounts, setAccounts] = useState<EliteCoreAccount[]>([]);
  const [owners, setOwners] = useState<CommandCentreInstanceUser[]>([]);
  const [instances, setInstances] = useState<CommandCentreInstance[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<EliteCoreAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [relationship, setRelationship] = useState('');
  const [salesStage, setSalesStage] = useState('');

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAccount, setNewAccount] = useState<AccountInput>({ ...EMPTY_NEW_ACCOUNT });

  const [showContactForm, setShowContactForm] = useState(false);
  const [contactDraft, setContactDraft] = useState<AccountContactInput>({
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    phone: '',
    isPrimary: false,
  });

  const [showOpportunityForm, setShowOpportunityForm] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [activityDraft, setActivityDraft] = useState<AccountActivityInput>({
    activityType: 'note',
    direction: 'internal',
    subject: '',
    summary: '',
  });
  const [showSignalForm, setShowSignalForm] = useState(false);
  const [signalDraft, setSignalDraft] = useState<AccountSignalInput>({
    signalType: 'requirement',
    value: '',
    strength: null,
    confidence: null,
  });
  const [instanceToLink, setInstanceToLink] = useState('');
  const [opportunityDraft, setOpportunityDraft] = useState<AccountOpportunityInput>({
    name: '',
    status: 'open',
    salesStage: 'qualified',
    estimatedArr: null,
    probability: null,
  });

  const loadAccounts = async () => {
    if (!isGlobal) return;
    const rows = await listAccounts({
      search: search.trim() || undefined,
      relationship: relationship || undefined,
      salesStage: salesStage || undefined,
      limit: 200,
    });
    setAccounts(rows);
  };

  useEffect(() => {
    if (!isGlobal) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([
      getAccountMeta(),
      listAccounts({ limit: 200 }),
      listInternalUsers(),
      listInstances(),
    ])
      .then(([metadata, accountRows, userRows, instanceRows]) => {
        if (cancelled) return;
        setMeta({ ...DEFAULT_META, ...metadata });
        setAccounts(accountRows);
        setOwners(userRows);
        setInstances(instanceRows);
      })
      .catch((error) => {
        if (!cancelled) setNotice(error?.message || 'Unable to load Elite Core growth accounts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGlobal]);

  useEffect(() => {
    if (!isGlobal) return undefined;
    const timer = window.setTimeout(() => {
      loadAccounts().catch((error) => {
        setNotice(error?.message || 'Unable to filter Accounts.');
      });
    }, 220);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, relationship, salesStage, isGlobal]);

  const metrics = useMemo(() => {
    const active = accounts.filter(isActiveProspect);
    const pipeline = active.reduce((sum, account) => {
      const value = account.openPipelineArr > 0 ? account.openPipelineArr : Number(account.estimatedArr || 0);
      return sum + value;
    }, 0);
    const won = accounts.filter((account) => account.relationship === 'customer').length;
    const now = Date.now();
    const actionNeeded = active.filter((account) => {
      if (!account.nextAction) return true;
      if (!account.nextActionAt) return false;
      return new Date(account.nextActionAt).getTime() <= now;
    }).length;
    return { active: active.length, pipeline, won, actionNeeded };
  }, [accounts]);

  const refreshSelected = async (accountId: number) => {
    setDetailLoading(true);
    try {
      const detail = await getAccount(accountId);
      setSelectedAccount(detail);
      setAccounts((rows) => rows.map((row) => row.id === detail.id ? { ...row, ...detail } : row));
    } finally {
      setDetailLoading(false);
    }
  };

  const openAccount = async (account: EliteCoreAccount) => {
    setSelectedAccount(account);
    await refreshSelected(account.id);
  };

  const handleCreateAccount = async (event: FormEvent) => {
    event.preventDefault();
    if (!newAccount.name?.trim() || creating) return;
    setCreating(true);
    setNotice(null);
    try {
      const created = await createAccount(newAccount);
      setAccounts((rows) => [created, ...rows]);
      setSelectedAccount(created);
      setShowCreate(false);
      setNewAccount({ ...EMPTY_NEW_ACCOUNT });
    } catch (error: any) {
      setNotice(error?.message || 'Unable to create Account.');
    } finally {
      setCreating(false);
    }
  };

  const handleStageChange = async (value: AccountSalesStage) => {
    if (!selectedAccount) return;
    try {
      const updated = await updateAccount(selectedAccount.id, { salesStage: value });
      setSelectedAccount(updated);
      setAccounts((rows) => rows.map((row) => row.id === updated.id ? { ...row, ...updated } : row));
    } catch (error: any) {
      setNotice(error?.message || 'Unable to update Account stage.');
    }
  };

  const handleLinkInstance = async () => {
    if (!selectedAccount || !instanceToLink) return;
    try {
      const updated = await linkAccountInstance(selectedAccount.id, Number(instanceToLink));
      setSelectedAccount(updated);
      setAccounts((rows) => rows.map((row) => row.id === updated.id ? { ...row, ...updated } : row));
      setInstanceToLink('');
    } catch (error: any) {
      setNotice(error?.message || 'Unable to link Elite Core instance.');
    }
  };

  const handleUnlinkInstance = async (instanceId: number) => {
    if (!selectedAccount) return;
    try {
      const updated = await unlinkAccountInstance(selectedAccount.id, instanceId);
      setSelectedAccount(updated);
      setAccounts((rows) => rows.map((row) => row.id === updated.id ? { ...row, ...updated } : row));
    } catch (error: any) {
      setNotice(error?.message || 'Unable to unlink Elite Core instance.');
    }
  };

  const handleAddContact = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedAccount || !contactDraft.firstName.trim()) return;
    try {
      await createAccountContact(selectedAccount.id, contactDraft);
      setContactDraft({ firstName: '', lastName: '', title: '', email: '', phone: '', isPrimary: false });
      setShowContactForm(false);
      await refreshSelected(selectedAccount.id);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to add Account contact.');
    }
  };

  const handleAddOpportunity = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedAccount || !opportunityDraft.name.trim()) return;
    try {
      await createAccountOpportunity(selectedAccount.id, opportunityDraft);
      setOpportunityDraft({
        name: '',
        status: 'open',
        salesStage: 'qualified',
        estimatedArr: null,
        probability: null,
      });
      setShowOpportunityForm(false);
      await refreshSelected(selectedAccount.id);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to add opportunity.');
    }
  };

  const handleAddActivity = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedAccount) return;
    if (!activityDraft.subject?.trim() && !activityDraft.summary?.trim()) return;
    try {
      await createAccountActivity(selectedAccount.id, activityDraft);
      setActivityDraft({
        activityType: 'note',
        direction: 'internal',
        subject: '',
        summary: '',
      });
      setShowActivityForm(false);
      await refreshSelected(selectedAccount.id);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to add Account activity.');
    }
  };

  const handleAddSignal = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedAccount || !signalDraft.value.trim()) return;
    try {
      await createAccountSignal(selectedAccount.id, signalDraft);
      setSignalDraft({
        signalType: 'requirement',
        value: '',
        strength: null,
        confidence: null,
      });
      setShowSignalForm(false);
      await refreshSelected(selectedAccount.id);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to add Account signal.');
    }
  };

  const handleResolveSignal = async (signalId: number) => {
    if (!selectedAccount) return;
    try {
      await updateAccountSignal(selectedAccount.id, signalId, { status: 'resolved' });
      await refreshSelected(selectedAccount.id);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to resolve Account signal.');
    }
  };

  if (!isGlobal) {
    return (
      <div className="growth-accounts-page">
        <section className="growth-access-card">
          <FiBriefcase />
          <div>
            <h1>Elite Core Growth</h1>
            <p>Accounts and Elite Core's corporate pipeline are available only to global Command Centre users.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="growth-accounts-page">
      <section className="growth-hero">
        <div>
          <div className="growth-eyebrow"><FiTrendingUp /> Elite Core growth</div>
          <h1>Accounts</h1>
          <p>
            The commercial layer above Instances. Track companies from first lead through evaluation,
            conversion, onboarding and long-term customer growth.
          </p>
        </div>
        <button className="growth-primary-button" type="button" onClick={() => setShowCreate(true)}>
          <FiPlus /> New account
        </button>
      </section>

      {notice ? (
        <div className="growth-notice">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><FiX /></button>
        </div>
      ) : null}

      <section className="growth-metrics" aria-label="Account growth summary">
        <article>
          <span className="growth-metric-icon growth-metric-icon--blue"><FiUsers /></span>
          <div><span>Active prospects</span><strong>{metrics.active}</strong></div>
        </article>
        <article>
          <span className="growth-metric-icon growth-metric-icon--green"><FiDollarSign /></span>
          <div><span>Visible pipeline</span><strong>{formatMoney(metrics.pipeline)}</strong></div>
        </article>
        <article>
          <span className="growth-metric-icon growth-metric-icon--purple"><FiCheckCircle /></span>
          <div><span>Customer accounts</span><strong>{metrics.won}</strong></div>
        </article>
        <article>
          <span className="growth-metric-icon growth-metric-icon--orange"><FiClock /></span>
          <div><span>Need next action</span><strong>{metrics.actionNeeded}</strong></div>
        </article>
      </section>

      <section className="growth-workspace">
        <div className="growth-list-card">
          <div className="growth-toolbar">
            <div className="growth-search">
              <FiSearch />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search company, website, source, current system..."
              />
            </div>
            <select value={relationship} onChange={(event) => setRelationship(event.target.value)}>
              <option value="">All relationships</option>
              {meta.relationships.map((value) => (
                <option key={value} value={value}>{titleCase(value)}</option>
              ))}
            </select>
            <select value={salesStage} onChange={(event) => setSalesStage(event.target.value)}>
              <option value="">All stages</option>
              {meta.salesStages.map((value) => (
                <option key={value} value={value}>{titleCase(value)}</option>
              ))}
            </select>
          </div>

          <div className="growth-table-wrap">
            <table className="growth-account-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Relationship</th>
                  <th>Stage</th>
                  <th>Owner</th>
                  <th>ARR / pipeline</th>
                  <th>Next action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="growth-empty">Loading Accounts…</td></tr>
                ) : accounts.length === 0 ? (
                  <tr><td colSpan={6} className="growth-empty">No Accounts match these filters.</td></tr>
                ) : accounts.map((account) => (
                  <tr
                    key={account.id}
                    onClick={() => openAccount(account)}
                    className={selectedAccount?.id === account.id ? 'is-selected' : ''}
                  >
                    <td>
                      <div className="growth-account-name">{account.name}</div>
                      <small>
                        {account.primaryContact?.name || account.website || account.currentSystem || 'No contact yet'}
                      </small>
                    </td>
                    <td><span className={`growth-chip relationship-${account.relationship}`}>{titleCase(account.relationship)}</span></td>
                    <td>{titleCase(account.salesStage)}</td>
                    <td>{account.owner?.name || 'Unassigned'}</td>
                    <td>
                      <strong>{formatMoney(account.openPipelineArr || account.estimatedArr)}</strong>
                      {account.probability != null ? <small>{account.probability}% probability</small> : null}
                    </td>
                    <td>
                      <div>{account.nextAction || 'Set next action'}</div>
                      {account.nextActionAt ? <small>{new Date(account.nextActionAt).toLocaleDateString()}</small> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <aside className={`growth-detail-panel ${selectedAccount ? 'is-open' : ''}`}>
          {!selectedAccount ? (
            <div className="growth-detail-empty">
              <FiBriefcase />
              <h2>Account 360</h2>
              <p>Select an Account to see the complete commercial relationship.</p>
            </div>
          ) : (
            <>
              <div className="growth-detail-header">
                <div>
                  <div className="growth-detail-kicker">{titleCase(selectedAccount.relationship)}</div>
                  <h2>{selectedAccount.name}</h2>
                  <p>{selectedAccount.website || selectedAccount.currentSystem || 'Elite Core growth account'}</p>
                </div>
                <button type="button" onClick={() => setSelectedAccount(null)} aria-label="Close Account 360">
                  <FiX />
                </button>
              </div>

              <div className="growth-detail-score-row">
                <div><span>Fit</span><strong>{selectedAccount.intelligence ? `${selectedAccount.intelligence.fit.score}%` : selectedAccount.fitScore != null ? `${selectedAccount.fitScore}%` : '—'}</strong></div>
                <div><span>Potential ARR</span><strong>{formatMoney(selectedAccount.estimatedArr || selectedAccount.openPipelineArr)}</strong></div>
                <div><span>Open opps</span><strong>{selectedAccount.openOpportunityCount}</strong></div>
              </div>

              {selectedAccount.intelligence ? (
                <section className="growth-ezee-card">
                  <div className="growth-ezee-heading">
                    <div>
                      <span className="growth-ezee-brand"><FiZap /> Ezee Intelligence</span>
                      <strong>{selectedAccount.intelligence.summary}</strong>
                    </div>
                    <span className="growth-readonly-badge">Read only</span>
                  </div>
                  <div className="growth-ezee-scores">
                    <div className={`growth-score growth-score--${scoreTone(selectedAccount.intelligence.fit.level)}`}>
                      <span>Fit</span>
                      <strong>{selectedAccount.intelligence.fit.score}</strong>
                      <small>{titleCase(selectedAccount.intelligence.fit.level)}</small>
                    </div>
                    <div className={`growth-score growth-score--${scoreTone(selectedAccount.intelligence.intent.level)}`}>
                      <span>Intent</span>
                      <strong>{selectedAccount.intelligence.intent.score}</strong>
                      <small>{titleCase(selectedAccount.intelligence.intent.level)}</small>
                    </div>
                    <div className={`growth-score growth-score--risk-${scoreTone(selectedAccount.intelligence.dealRisk.level)}`}>
                      <span>Deal risk</span>
                      <strong>{selectedAccount.intelligence.dealRisk.score}</strong>
                      <small>{titleCase(selectedAccount.intelligence.dealRisk.level)}</small>
                    </div>
                  </div>
                  <div className="growth-ezee-next">
                    <FiTarget />
                    <div>
                      <span>Ezee recommends next</span>
                      <strong>{selectedAccount.intelligence.nextAction.action}</strong>
                      <p>{selectedAccount.intelligence.nextAction.reason}</p>
                    </div>
                  </div>
                  {selectedAccount.intelligence.blockers.length ? (
                    <div className="growth-ezee-blockers">
                      <span><FiAlertTriangle /> Active blockers</span>
                      {selectedAccount.intelligence.blockers.map((blocker, index) => (
                        <p key={`${blocker.type}-${blocker.value}-${index}`}>
                          <strong>{titleCase(blocker.type)}</strong> · {blocker.value}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </section>
              ) : null}

              <section className="growth-detail-section">
                <div className="growth-section-heading">
                  <div><span>Sales position</span><strong>{titleCase(selectedAccount.salesStage)}</strong></div>
                </div>
                <label>
                  Stage
                  <select
                    value={selectedAccount.salesStage}
                    onChange={(event) => handleStageChange(event.target.value as AccountSalesStage)}
                    disabled={detailLoading}
                  >
                    {meta.salesStages.map((value) => (
                      <option key={value} value={value}>{titleCase(value)}</option>
                    ))}
                  </select>
                </label>
                <div className="growth-detail-grid">
                  <div><span>Owner</span><strong>{selectedAccount.owner?.name || 'Unassigned'}</strong></div>
                  <div><span>Source</span><strong>{selectedAccount.source || '—'}</strong></div>
                  <div><span>Current system</span><strong>{selectedAccount.currentSystem || '—'}</strong></div>
                  <div><span>Employees</span><strong>{selectedAccount.employeeCount ?? '—'}</strong></div>
                </div>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading">
                  <div><span>Planned next move</span><strong>{selectedAccount.nextAction || 'Not set'}</strong></div>
                </div>
                <p className="growth-muted">
                  {selectedAccount.nextActionAt
                    ? `Target: ${new Date(selectedAccount.nextActionAt).toLocaleString()}`
                    : 'No dated next action is currently assigned.'}
                </p>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading">
                  <div>
                    <span>Elite Core instances</span>
                    <strong>{selectedAccount.instances?.length ? `${selectedAccount.instances.length} linked` : 'Not linked'}</strong>
                  </div>
                </div>
                <div className="growth-instance-linker">
                  <select value={instanceToLink} onChange={(event) => setInstanceToLink(event.target.value)}>
                    <option value="">Select an Instance to link…</option>
                    {instances
                      .filter((instance) => !(selectedAccount.instances || []).some((linked) => linked.id === instance.id))
                      .map((instance) => (
                        <option key={instance.id} value={instance.id}>{instance.displayName}</option>
                      ))}
                  </select>
                  <button type="button" onClick={handleLinkInstance} disabled={!instanceToLink}>
                    <FiLink /> Link
                  </button>
                </div>
                <p className="growth-muted">Linking the first production Instance promotes the Account to Customer / Won. One Account can own multiple Instances.</p>
                <div className="growth-instance-list">
                  {(selectedAccount.instances || []).map((instance) => (
                    <div className="growth-linked-instance" key={instance.id}>
                      <FiLink />
                      <div>
                        <strong>{instance.name}</strong>
                        <span>{instance.instanceUniqueId}</span>
                      </div>
                      <button type="button" onClick={() => handleUnlinkInstance(instance.id)}>Unlink</button>
                    </div>
                  ))}
                </div>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading growth-section-heading--actions">
                  <div><span>Contacts</span><strong>{selectedAccount.contacts?.length || 0}</strong></div>
                  <button type="button" onClick={() => setShowContactForm((value) => !value)}><FiPlus /> Add</button>
                </div>
                {showContactForm ? (
                  <form className="growth-inline-form" onSubmit={handleAddContact}>
                    <div className="growth-form-row">
                      <input required placeholder="First name" value={contactDraft.firstName} onChange={(e) => setContactDraft((v) => ({ ...v, firstName: e.target.value }))} />
                      <input placeholder="Last name" value={contactDraft.lastName || ''} onChange={(e) => setContactDraft((v) => ({ ...v, lastName: e.target.value }))} />
                    </div>
                    <input placeholder="Title" value={contactDraft.title || ''} onChange={(e) => setContactDraft((v) => ({ ...v, title: e.target.value }))} />
                    <input type="email" placeholder="Email" value={contactDraft.email || ''} onChange={(e) => setContactDraft((v) => ({ ...v, email: e.target.value }))} />
                    <div className="growth-form-actions">
                      <label className="growth-check"><input type="checkbox" checked={contactDraft.isPrimary || false} onChange={(e) => setContactDraft((v) => ({ ...v, isPrimary: e.target.checked }))} /> Primary</label>
                      <button type="submit">Add contact</button>
                    </div>
                  </form>
                ) : null}
                <div className="growth-contact-list">
                  {(selectedAccount.contacts || []).map((contact) => (
                    <article key={contact.id}>
                      <span className="growth-contact-avatar">{(contact.firstName?.[0] || '?').toUpperCase()}</span>
                      <div>
                        <strong>{contact.name}{contact.isPrimary ? ' · Primary' : ''}</strong>
                        <span>{contact.title || contact.email || 'Contact'}</span>
                        {contact.email && contact.title ? <small>{contact.email}</small> : null}
                      </div>
                    </article>
                  ))}
                  {!selectedAccount.contacts?.length ? <p className="growth-muted">No contacts yet.</p> : null}
                </div>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading growth-section-heading--actions">
                  <div><span>Opportunities</span><strong>{selectedAccount.opportunities?.length || 0}</strong></div>
                  <button type="button" onClick={() => setShowOpportunityForm((value) => !value)}><FiPlus /> Add</button>
                </div>
                {showOpportunityForm ? (
                  <form className="growth-inline-form" onSubmit={handleAddOpportunity}>
                    <input required placeholder="Opportunity name" value={opportunityDraft.name} onChange={(e) => setOpportunityDraft((v) => ({ ...v, name: e.target.value }))} />
                    <div className="growth-form-row">
                      <select value={opportunityDraft.salesStage} onChange={(e) => setOpportunityDraft((v) => ({ ...v, salesStage: e.target.value as AccountSalesStage }))}>
                        {meta.salesStages.filter((stage) => !['new', 'contacted', 'lost'].includes(stage)).map((stage) => (
                          <option key={stage} value={stage}>{titleCase(stage)}</option>
                        ))}
                      </select>
                      <input type="number" min="0" placeholder="Est. ARR" value={opportunityDraft.estimatedArr ?? ''} onChange={(e) => setOpportunityDraft((v) => ({ ...v, estimatedArr: e.target.value ? Number(e.target.value) : null }))} />
                    </div>
                    <div className="growth-form-row">
                      <input type="number" min="0" max="100" placeholder="Probability %" value={opportunityDraft.probability ?? ''} onChange={(e) => setOpportunityDraft((v) => ({ ...v, probability: e.target.value ? Number(e.target.value) : null }))} />
                      <select value={opportunityDraft.status} onChange={(e) => setOpportunityDraft((v) => ({ ...v, status: e.target.value as AccountOpportunityInput['status'] }))}>
                        {meta.opportunityStatuses.map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
                      </select>
                    </div>
                    <div className="growth-form-actions">
                      <span />
                      <button type="submit">Add opportunity</button>
                    </div>
                  </form>
                ) : null}
                <div className="growth-opportunity-list">
                  {(selectedAccount.opportunities || []).map((opportunity) => (
                    <article key={opportunity.id}>
                      <div>
                        <strong>{opportunity.name}</strong>
                        <span>{titleCase(opportunity.salesStage)} · {titleCase(opportunity.status)}</span>
                      </div>
                      <div className="growth-opportunity-value">
                        <strong>{formatMoney(opportunity.estimatedArr)}</strong>
                        <small>{opportunity.probability != null ? `${opportunity.probability}%` : '—'}</small>
                      </div>
                    </article>
                  ))}
                  {!selectedAccount.opportunities?.length ? <p className="growth-muted">No opportunities yet.</p> : null}
                </div>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading growth-section-heading--actions">
                  <div>
                    <span>Signals</span>
                    <strong>{(selectedAccount.signals || []).filter((signal) => signal.status === 'active').length} active</strong>
                  </div>
                  <button type="button" onClick={() => setShowSignalForm((value) => !value)}><FiPlus /> Add</button>
                </div>
                {showSignalForm ? (
                  <form className="growth-inline-form" onSubmit={handleAddSignal}>
                    <select
                      value={signalDraft.signalType}
                      onChange={(event) => setSignalDraft((value) => ({ ...value, signalType: event.target.value as AccountSignalInput['signalType'] }))}
                    >
                      {meta.signalTypes.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
                    </select>
                    <textarea
                      required
                      rows={3}
                      placeholder="What did we learn?"
                      value={signalDraft.value}
                      onChange={(event) => setSignalDraft((value) => ({ ...value, value: event.target.value }))}
                    />
                    <div className="growth-form-row">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Strength %"
                        value={signalDraft.strength ?? ''}
                        onChange={(event) => setSignalDraft((value) => ({ ...value, strength: event.target.value ? Number(event.target.value) : null }))}
                      />
                      <input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="Confidence %"
                        value={signalDraft.confidence ?? ''}
                        onChange={(event) => setSignalDraft((value) => ({ ...value, confidence: event.target.value ? Number(event.target.value) : null }))}
                      />
                    </div>
                    <div className="growth-form-actions"><span /><button type="submit">Add signal</button></div>
                  </form>
                ) : null}
                <div className="growth-signal-list">
                  {(selectedAccount.signals || []).filter((signal) => signal.status === 'active').map((signal) => (
                    <article key={signal.id} className={`growth-signal growth-signal--${signal.signalType}`}>
                      <div>
                        <span>{titleCase(signal.signalType)}</span>
                        <strong>{signal.value}</strong>
                        {(signal.strength != null || signal.confidence != null) ? (
                          <small>
                            {signal.strength != null ? `Strength ${signal.strength}%` : ''}
                            {signal.strength != null && signal.confidence != null ? ' · ' : ''}
                            {signal.confidence != null ? `Confidence ${signal.confidence}%` : ''}
                          </small>
                        ) : null}
                      </div>
                      <button type="button" onClick={() => handleResolveSignal(signal.id)}>Resolve</button>
                    </article>
                  ))}
                  {!(selectedAccount.signals || []).some((signal) => signal.status === 'active') ? (
                    <p className="growth-muted">No active requirements, objections, buying signals, or blockers yet.</p>
                  ) : null}
                </div>
              </section>

              <section className="growth-detail-section">
                <div className="growth-section-heading growth-section-heading--actions">
                  <div>
                    <span>Activity timeline</span>
                    <strong>{selectedAccount.activities?.length || 0} events</strong>
                  </div>
                  <button type="button" onClick={() => setShowActivityForm((value) => !value)}><FiPlus /> Add</button>
                </div>
                {showActivityForm ? (
                  <form className="growth-inline-form" onSubmit={handleAddActivity}>
                    <div className="growth-form-row">
                      <select
                        value={activityDraft.activityType}
                        onChange={(event) => setActivityDraft((value) => ({ ...value, activityType: event.target.value as AccountActivityInput['activityType'] }))}
                      >
                        {meta.activityTypes
                          .filter((type) => !['stage-change', 'contact-added', 'opportunity-created', 'opportunity-updated', 'instance-linked', 'instance-unlinked', 'signal-added', 'signal-resolved', 'system'].includes(type))
                          .map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
                      </select>
                      <select
                        value={activityDraft.direction || 'internal'}
                        onChange={(event) => setActivityDraft((value) => ({ ...value, direction: event.target.value as AccountActivityInput['direction'] }))}
                      >
                        {meta.activityDirections.map((direction) => <option key={direction} value={direction}>{titleCase(direction)}</option>)}
                      </select>
                    </div>
                    <input
                      placeholder="Subject"
                      value={activityDraft.subject || ''}
                      onChange={(event) => setActivityDraft((value) => ({ ...value, subject: event.target.value }))}
                    />
                    <textarea
                      rows={3}
                      placeholder="Summary / outcome"
                      value={activityDraft.summary || ''}
                      onChange={(event) => setActivityDraft((value) => ({ ...value, summary: event.target.value }))}
                    />
                    <div className="growth-form-actions"><span /><button type="submit">Add activity</button></div>
                  </form>
                ) : null}
                <div className="growth-timeline">
                  {(selectedAccount.activities || []).map((activity) => (
                    <article key={activity.id}>
                      <span className="growth-timeline-icon"><FiActivity /></span>
                      <div>
                        <div className="growth-timeline-meta">
                          <strong>{activity.subject || titleCase(activity.activityType)}</strong>
                          <span>{formatActivityTime(activity.occurredAt)}</span>
                        </div>
                        {activity.summary ? <p>{activity.summary}</p> : null}
                        <small>
                          {titleCase(activity.activityType)}
                          {activity.direction ? ` · ${titleCase(activity.direction)}` : ''}
                          {activity.createdBy?.name ? ` · ${activity.createdBy.name}` : ''}
                        </small>
                      </div>
                    </article>
                  ))}
                  {!selectedAccount.activities?.length ? <p className="growth-muted">No Account activity recorded yet.</p> : null}
                </div>
              </section>

              {(selectedAccount.painPoints?.length || selectedAccount.objections?.length) ? (
                <section className="growth-detail-section">
                  <div className="growth-section-heading"><div><span>What we know</span><strong>Sales intelligence</strong></div></div>
                  {selectedAccount.painPoints?.length ? (
                    <div className="growth-intel-block"><span>Pain points</span>{selectedAccount.painPoints.map((item) => <p key={item}>{item}</p>)}</div>
                  ) : null}
                  {selectedAccount.objections?.length ? (
                    <div className="growth-intel-block"><span>Objections</span>{selectedAccount.objections.map((item) => <p key={item}>{item}</p>)}</div>
                  ) : null}
                </section>
              ) : null}
            </>
          )}
        </aside>
      </section>

      {showCreate ? (
        <div className="growth-modal-backdrop" role="presentation" onMouseDown={() => setShowCreate(false)}>
          <form className="growth-create-modal" onSubmit={handleCreateAccount} onMouseDown={(event) => event.stopPropagation()}>
            <div className="growth-create-header">
              <div><span>Elite Core Growth</span><h2>New Account</h2></div>
              <button type="button" onClick={() => setShowCreate(false)} aria-label="Close"><FiX /></button>
            </div>
            <p>Create the company relationship first. Opportunities, contacts, migration data and a future Instance stay connected to this Account.</p>
            <label>Company name<input required autoFocus value={newAccount.name || ''} onChange={(e) => setNewAccount((v) => ({ ...v, name: e.target.value }))} /></label>
            <div className="growth-form-row">
              <label>Relationship<select value={newAccount.relationship} onChange={(e) => setNewAccount((v) => ({ ...v, relationship: e.target.value as AccountInput['relationship'] }))}>{meta.relationships.map((value) => <option key={value} value={value}>{titleCase(value)}</option>)}</select></label>
              <label>Sales stage<select value={newAccount.salesStage} onChange={(e) => setNewAccount((v) => ({ ...v, salesStage: e.target.value as AccountSalesStage }))}>{meta.salesStages.map((value) => <option key={value} value={value}>{titleCase(value)}</option>)}</select></label>
            </div>
            <div className="growth-form-row">
              <label>Website<input value={newAccount.website || ''} onChange={(e) => setNewAccount((v) => ({ ...v, website: e.target.value }))} /></label>
              <label>Source<input placeholder="Referral, event, web..." value={newAccount.source || ''} onChange={(e) => setNewAccount((v) => ({ ...v, source: e.target.value }))} /></label>
            </div>
            <div className="growth-form-row">
              <label>Current system<input value={newAccount.currentSystem || ''} onChange={(e) => setNewAccount((v) => ({ ...v, currentSystem: e.target.value }))} /></label>
              <label>Estimated ARR<input type="number" min="0" value={newAccount.estimatedArr ?? ''} onChange={(e) => setNewAccount((v) => ({ ...v, estimatedArr: e.target.value ? Number(e.target.value) : null }))} /></label>
            </div>
            <label>Owner<select value={newAccount.ownerUserId || ''} onChange={(e) => setNewAccount((v) => ({ ...v, ownerUserId: e.target.value ? Number(e.target.value) : null }))}><option value="">Unassigned</option>{owners.map((owner) => <option key={owner.id} value={owner.id}>{[owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.email}</option>)}</select></label>
            <div className="growth-create-actions">
              <button type="button" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="growth-primary-button" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create Account'} <FiArrowUpRight /></button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default AccountsPage;
